import { Link } from "react-router-dom";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SeoTags } from "../../components/Head/SeoTags";

const Terms = () => {
  const { i18n } = useTranslation();
  const lastUpdated = new Date("2025-07-02").toLocaleDateString(i18n.language, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const [openAccordion, setOpenAccordion] = useState("disclaimers");

  const toggleAccordion = (accordionId) => {
    setOpenAccordion(openAccordion === accordionId ? null : accordionId);
  };

  return (
    <>
      <SeoTags
        title="Terms of Service - onBookmarks | Clear & Fair Terms"
        description="Read our straightforward terms of service. We believe in transparency - understand your rights and our commitments when using onBookmarks."
        keywords="terms of service, user agreement, legal terms, onBookmarks, fair terms, transparent policies"
        canonical="https://onbookmarks.com/legal/terms-of-service/"
        localized={false}
      />

      {/* Enhanced WebPage Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Terms of Service - onBookmarks",
            description:
              "Clear and fair terms of service for onBookmarks users. Transparent policies written in plain language.",
            url: "https://onbookmarks.com/legal/terms-of-service",
            isPartOf: {
              "@type": "WebSite",
              name: "onBookmarks",
              url: "https://onbookmarks.com",
            },
            lastReviewed: lastUpdated,
            publisher: {
              "@type": "Organization",
              name: "onBookmarks",
            },
            about: {
              "@type": "Thing",
              name: "Service Agreement",
            },
          }),
        }}
      />

      <div className="min-vh-100">
        {/* Hero Section */}
        <div className="position-relative overflow-hidden bg-gradient-green text-white py-5">
          <div className="container py-lg-5">
            <div className="row justify-content-center">
              <div className="col-lg-8 text-center">
                <div className="d-inline-flex align-items-center bg-white bg-opacity-25 rounded-pill px-3 py-2 mb-4">
                  <i className="bi bi-file-text-fill me-2"></i>
                  <span className="small fw-medium">Updated {lastUpdated}</span>
                </div>

                <h1 className="display-4 fw-bold mb-4 text-white">
                  Terms of Service
                </h1>
                <p className="lead mx-auto" style={{ maxWidth: "42rem" }}>
                  We believe in fair, transparent terms. Here's what you need to
                  know about using onBookmarks - written in plain English.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Summary Cards */}
        <div
          className="container"
          style={{ marginTop: "-3rem", position: "relative", zIndex: 10 }}
        >
          <div className="row g-4 justify-content-center">
            <div className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-lg h-100">
                <div className="card-body p-4">
                  <div
                    className="bg-primary bg-opacity-10 rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "48px", height: "48px" }}
                  >
                    <i className="bi bi-person-check-fill fs-4 text-primary"></i>
                  </div>
                  <h3 className="h5 fw-semibold mb-2">Age Requirement: 16+</h3>
                  <p className="text-muted mb-0">
                    You must be 16 or older to use onBookmarks
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-lg h-100">
                <div className="card-body p-4">
                  <div
                    className="bg-success bg-opacity-10 rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "48px", height: "48px" }}
                  >
                    <i className="bi bi-shield-check fs-4 text-success"></i>
                  </div>
                  <h3 className="h5 fw-semibold mb-2">Your Content is Yours</h3>
                  <p className="text-muted mb-0">
                    We don't claim ownership of your bookmarks or data
                  </p>
                </div>
              </div>
            </div>

            <div className="col-md-6 col-lg-4">
              <div className="card border-0 shadow-lg h-100">
                <div className="card-body p-4">
                  <div
                    className="bg-warning bg-opacity-10 rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                    style={{ width: "48px", height: "48px" }}
                  >
                    <i className="bi bi-arrow-repeat fs-4 text-warning"></i>
                  </div>
                  <h3 className="h5 fw-semibold mb-2">30-Day Money Back</h3>
                  <p className="text-muted mb-0">
                    New users can get a full refund within 30 days
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container py-5">
          {/* Introduction */}
          <section className="mb-5">
            <p className="lead text-muted">
              By using onBookmarks, you agree to these terms. We've written them
              to be as clear and fair as possible. If something doesn't make
              sense, please <a href="mailto:support@onbookmarks.com">ask us</a>.
            </p>
            <p className="text-muted">
              <strong>The Short Version:</strong> Be respectful, pay for what
              you use, don't break the law, and remember that your encrypted
              bookmarks are your responsibility. We'll provide a reliable
              service and protect your privacy.
            </p>

            <div
              className="alert alert-info d-flex align-items-start mt-4"
              role="alert"
            >
              <i className="bi bi-info-circle-fill me-2"></i>
              <div>
                <strong>Important:</strong> If you're using onBookmarks for a
                company, you must have authority to agree to these terms on
                their behalf.
              </div>
            </div>
          </section>

          {/* Account Terms */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-person-badge fs-3 text-primary me-3"></i>
              Account Requirements
            </h2>

            <div className="bg-primary bg-opacity-10 rounded-3 p-4">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="bg-white bg-opacity-75 rounded-3 p-3">
                    <h4 className="h6 fw-medium mb-3">To create an account:</h4>
                    <ul className="list-unstyled text-muted mb-0">
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Be 16 years or older
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Be a human (no bots!)
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        Provide valid email
                      </li>
                      <li>
                        <i className="bi bi-check-circle-fill text-success me-2"></i>
                        One person per account
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="bg-white bg-opacity-75 rounded-3 p-3">
                    <h4 className="h6 fw-medium mb-3">
                      Your responsibilities:
                    </h4>
                    <ul className="list-unstyled text-muted mb-0">
                      <li className="mb-2">
                        <i className="bi bi-shield-lock text-primary me-2"></i>
                        Keep your password secure
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-person-check text-primary me-2"></i>
                        Monitor account activity
                      </li>
                      <li className="mb-2">
                        <i className="bi bi-flag text-primary me-2"></i>
                        Follow applicable laws
                      </li>
                      <li>
                        <i className="bi bi-bookmark-check text-primary me-2"></i>
                        Manage your content
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div
                className="alert alert-warning d-flex align-items-center mt-3 mb-0"
                role="alert"
              >
                <i className="bi bi-exclamation-triangle me-2"></i>
                <small>
                  <strong>Note:</strong> You're responsible for all activity
                  under your account, even if someone else posts it.
                </small>
              </div>
            </div>
          </section>

          {/* Payments & Billing */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-credit-card fs-3 text-success me-3"></i>
              Payments & Billing
            </h2>

            <div className="row g-4">
              <div className="col-12">
                <div className="bg-success bg-opacity-10 rounded-3 p-4">
                  <h3 className="h5 fw-semibold mb-3">
                    <i className="bi bi-cash-coin me-2"></i>
                    Pricing & Refunds
                  </h3>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <div className="card border-success">
                        <div className="card-body">
                          <h4 className="h6 fw-semibold text-success mb-2">
                            <i className="bi bi-emoji-smile me-1"></i>
                            30-Day Money Back Guarantee
                          </h4>
                          <p className="text-muted mb-0">
                            New users can request a full refund within 30 days
                            by emailing support@onbookmarks.com
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="card">
                        <div className="card-body">
                          <h4 className="h6 fw-semibold mb-2 mt-2">
                            <i className="bi bi-calendar-check me-1"></i>
                            Billing Cycle
                          </h4>
                          <p className="text-muted mb-2">
                            Billed monthly or annually in advance. No refunds
                            after 30 days.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <h4 className="h6 fw-medium mb-2">
                      Important billing notes:
                    </h4>
                    <ul className="small text-muted mb-0">
                      <li>Free accounts don't need payment info</li>
                      <li>Upgrades are billed immediately</li>
                      <li>Prices exclude applicable taxes</li>
                      <li>Downgrading may reduce features</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Content & Ownership */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-bookmark-star fs-3 text-primary me-3"></i>
              Your Content & Privacy
            </h2>

            <div className="bg-gradient-to-r from-primary bg-opacity-10 to-success bg-opacity-10 rounded-3 p-4">
              <div className="row align-items-center g-4">
                <div className="col-md-6">
                  <h3 className="h5 fw-semibold mb-3 text-white">
                    <i className="bi bi-shield-fill-check text-success me-2"></i>
                    Your Content Remains Yours
                  </h3>
                  <p className="text-white mb-0">
                    We don't claim any ownership of your bookmarks, notes, or
                    other content. It's yours, period.
                  </p>
                </div>

                <div className="col-md-6">
                  <h3 className="h5 fw-semibold mb-3 text-white">
                    <i className="bi bi-eye-slash text-white me-2"></i>
                    Public vs Private
                  </h3>
                  <p className="text-white mb-0">
                    You control visibility. Public bookmarks can be viewed by
                    others. Private and encrypted bookmarks stay private.
                  </p>
                </div>
              </div>

              <div className="mt-4 p-3 bg-white bg-opacity-75 rounded">
                <p className="mb-0">
                  <i className="bi bi-info-circle text-primary me-2"></i>
                  <strong>Legal stuff:</strong> You're responsible for your
                  content. Don't post anything illegal or that violates others'
                  rights. We can remove content that violates these terms.
                </p>
              </div>
            </div>
          </section>

          {/* Encrypted Content */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-lock fs-3 text-warning me-3"></i>
              Encrypted Bookmarks
            </h2>

            <div className="bg-warning bg-opacity-10 rounded-3 p-4">
              <div
                className="alert alert-warning d-flex align-items-start mb-3"
                role="alert"
              >
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                <div>
                  <strong>Critical:</strong> We cannot recover lost encryption
                  keys. Store them safely!
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <h4 className="h6 fw-semibold mb-2">What we CAN'T do:</h4>
                  <ul className="list-unstyled text-muted mb-0">
                    <li className="mb-2">
                      <i className="bi bi-x-circle text-danger me-2"></i>
                      Access your encrypted content
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-x-circle text-danger me-2"></i>
                      Recover lost encryption keys
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-x-circle text-danger me-2"></i>
                      Decrypt your bookmarks
                    </li>
                    <li>
                      <i className="bi bi-x-circle text-danger me-2"></i>
                      See what you've encrypted
                    </li>
                  </ul>
                </div>

                <div className="col-md-6">
                  <h4 className="h6 fw-semibold mb-2">
                    Your responsibilities:
                  </h4>
                  <ul className="list-unstyled text-muted mb-0">
                    <li className="mb-2">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Keep encryption keys safe
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Use legal content only
                    </li>
                    <li className="mb-2">
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Make secure backups
                    </li>
                    <li>
                      <i className="bi bi-check-circle text-success me-2"></i>
                      Remember: it's irreversible
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Cancellation */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-x-circle fs-3 text-danger me-3"></i>
              Cancellation & Termination
            </h2>

            <div className="row g-4">
              <div className="col-md-6">
                <div className="bg-danger bg-opacity-10 rounded-3 p-4 h-100">
                  <h3 className="h5 fw-semibold mb-3 text-danger">
                    <i className="bi bi-person-x me-2"></i>
                    Cancelling Your Account
                  </h3>
                  <ol className="text-muted mb-0">
                    <li className="mb-2">Go to Settings → Subscription</li>
                    <li className="mb-2">Click "Cancel Subscription"</li>
                    <li className="mb-2">
                      Benefits continue until period ends
                    </li>
                    <li>No charges after cancellation</li>
                  </ol>
                  <p className="small text-muted mt-3 mb-0">
                    <strong>Note:</strong> Email requests don't count as
                    cancellation. Use the settings page.
                  </p>
                </div>
              </div>

              <div className="col-md-6">
                <div className="bg-warning bg-opacity-10 rounded-3 p-4 h-100">
                  <h3 className="h5 fw-semibold mb-3 text-warning">
                    <i className="bi bi-trash3 me-2"></i>
                    Deleting Your Account
                  </h3>
                  <p className="text-muted mb-3">
                    Want to permanently delete everything?
                  </p>
                  <ol className="text-muted mb-0">
                    <li className="mb-2">Go to Account Settings</li>
                    <li className="mb-2">Click "Delete Account"</li>
                    <li className="mb-2">Confirm deletion</li>
                    <li>All data is permanently erased</li>
                  </ol>
                  <div
                    className="alert alert-warning d-flex align-items-center mt-3 mb-0"
                    role="alert"
                  >
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    <span>This cannot be undone!</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4 p-3 bg-light rounded">
              <p className="text-muted mb-0">
                <i className="bi bi-info-circle me-2"></i>
                We reserve the right to suspend accounts that violate these
                terms, but we'll try to work with you first if there's an issue.
              </p>
            </div>
          </section>

          {/* General Conditions */}
          <section className="mb-5">
            <h2 className="h3 fw-bold mb-4 d-flex align-items-center">
              <i className="bi bi-clipboard-check fs-3 text-primary me-3"></i>
              The Legal Stuff
            </h2>

            <div className="accordion" id="legalAccordion">
              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${
                      openAccordion === "disclaimers" ? "" : "collapsed"
                    }`}
                    type="button"
                    onClick={() => toggleAccordion("disclaimers")}
                    aria-expanded={openAccordion === "disclaimers"}
                  >
                    <i className="bi bi-shield-exclamation me-2"></i>
                    Service Disclaimers
                  </button>
                </h2>
                <div
                  id="disclaimers"
                  className={`accordion-collapse ${
                    openAccordion === "disclaimers"
                      ? "collapse show"
                      : "collapse"
                  }`}
                >
                  <div className="accordion-body">
                    <p className="text-muted">
                      We work hard to provide a great service, but:
                    </p>
                    <ul className="text-muted">
                      <li>The service is provided "as is"</li>
                      <li>We can't guarantee 100% uptime</li>
                      <li>
                        We're not liable for data loss (backup important stuff!)
                      </li>
                      <li>Support is in English only</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="accordion-item">
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${
                      openAccordion === "governing" ? "" : "collapsed"
                    }`}
                    type="button"
                    onClick={() => toggleAccordion("governing")}
                    aria-expanded={openAccordion === "governing"}
                  >
                    <i className="bi bi-geo-alt me-2"></i>
                    Governing Law
                  </button>
                </h2>
                <div
                  id="governing"
                  className={`accordion-collapse ${
                    openAccordion === "governing" ? "collapse show" : "collapse"
                  }`}
                >
                  <div className="accordion-body">
                    <p className="text-muted mb-0">
                      These terms are governed by New York law. Any disputes
                      will be resolved in New York courts.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Quick Actions */}
          <div className="bg-light rounded-3 p-4 p-lg-5">
            <h3 className="h4 fw-semibold mb-4 text-center">
              Related Documents
            </h3>
            <div className="row g-3 justify-content-center">
              <div className="col-md-4">
                <Link
                  to="/legal/privacy-policy"
                  className="card text-center text-decoration-none h-100 border-0 shadow-sm"
                >
                  <div className="card-body py-4">
                    <i className="bi bi-shield-check fs-2 text-primary d-block mb-2"></i>
                    <p className="fw-medium mb-0">Privacy Policy</p>
                  </div>
                </Link>
              </div>

              <div className="col-md-4">
                <Link
                  to="/pricing"
                  className="card text-center text-decoration-none h-100 border-0 shadow-sm"
                >
                  <div className="card-body py-4">
                    <i className="bi bi-tag fs-2 text-success d-block mb-2"></i>
                    <p className="fw-medium mb-0">See Pricing</p>
                  </div>
                </Link>
              </div>

              <div className="col-md-4">
                <a
                  href="https://my.onbookmarks.com/register"
                  className="card text-center text-decoration-none h-100 border-0 shadow-sm"
                >
                  <div className="card-body py-4">
                    <i className="bi bi-rocket-takeoff fs-2 text-warning d-block mb-2"></i>
                    <p className="fw-medium mb-0">Get Started</p>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Terms;
